import React from "react";

import { ArrowRight } from "lucide-react";

import CommonModal from "../../Components/Modal";

export type PairsFocusModalKind =
  | "no_mappings"
  | "all_stable"
  | "mixed"
  | "all_needs_attention";

type Props = {
  kind: PairsFocusModalKind | null;
  accountLabel: string;
  /** My mapped pairs for this account (aligned with List Pairs: mapped/cap). */
  attentionCount?: number;
  totalCount?: number;
  stableCount?: number;
  onStayHere: () => void;
  onGoToRelationshipPriorities: () => void;
  onSeeAllRelationships: () => void;
};

const TITLE: Record<PairsFocusModalKind, string> = {
  no_mappings: "No pairs for a given account",
  all_stable: "All are stable",
  mixed: "Mix scenario (combination of stable and need attention)",
  all_needs_attention: "All relationship pairs need attention",
};

/** Go to Relationship Priorities only when there is Needs Attention work. */
const showGoToRp = (kind: PairsFocusModalKind | null) =>
  kind === "mixed" || kind === "all_needs_attention";

/**
 * Shown when opening Pairs from Account Priorities.
 *
 * - No data: Stay / See all
 * - All stable: Stay / See all
 * - Mixed or all Needs Attention: Stay / Go to RP / See all
 */
const accountName = (name: string) =>
  name ? (
    <span className="font-semibold text-gray-800">{name}</span>
  ) : (
    <>this account</>
  );

const PairsFocusModal: React.FC<Props> = ({
  kind,
  accountLabel,
  onStayHere,
  onGoToRelationshipPriorities,
  onSeeAllRelationships,
}) => {
  const name = accountLabel.trim();

  const body = (() => {
    if (kind === "no_mappings") {
      return (
        <>
          You currently do not own any relationships in{" "}
          {name ? <>the {accountName(name)} account</> : <>this account</>}. To
          get started map one or more contacts in the Account Relationships or
          stay on Account Priorities.
        </>
      );
    }

    if (kind === "all_stable") {
      return (
        <>
          All relationships that you own in {accountName(name)} are{" "}
          <span className="font-semibold text-gray-800">Stable</span> and{" "}
          <span className="font-semibold text-gray-800">Healthy</span> — none of
          them are Unassessed, have No Goal Set, or Low Score. Review Account
          Relationships or stay here.
        </>
      );
    }

    if (kind === "mixed") {
      return (
        <>
          Some of your relationships on {accountName(name)} need attention.
          Visit Relationship Priorities to work the ones that need attention,
          review all of your Account Relationships, or stay here.
        </>
      );
    }

    // all_needs_attention
    return (
      <>
        All your relationships on {accountName(name)} need attention — for
        assessment, goal setting, or due to low scores. Visit Relationship
        Priorities to review them, go to Account Relationships, or stay here.
      </>
    );
  })();

  return (
    <CommonModal
      isOpen={kind != null}
      toggle={onStayHere}
      onCloseButton={onStayHere}
      size="xl"
      usePortal
      title={kind ? TITLE[kind] : ""}
      footer={
        <div className="flex w-full flex-nowrap items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
            onClick={onStayHere}
          >
            Stay here
          </button>
          {showGoToRp(kind) && (
            <button
              type="button"
              className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-[12.5px] font-semibold text-purple-700 transition-colors hover:border-purple-300 hover:bg-purple-100"
              onClick={onGoToRelationshipPriorities}
            >
              Go to Relationship Priorities
            </button>
          )}
          <button
            type="button"
            className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-purple-600 bg-purple-600 px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:border-purple-700 hover:bg-purple-700"
            onClick={onSeeAllRelationships}
          >
            See all Relationships
            <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </button>
        </div>
      }
    >
      <p className="m-0 text-[13.5px] leading-relaxed text-gray-600">{body}</p>
    </CommonModal>
  );
};

export default PairsFocusModal;
